<template>
    <div class="sinter-tabelas-codigo-page">
        <a-spin tip="Carregando..." :spinning="spinning">
            <a-config-provider :locale="currentLocale">
                <a-card>
                    <template #title>
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item>
                                <span>Sinter</span>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item>Tabelas de Código</a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>

                    <a-table
                        :columns="catalogColumns"
                        :data-source="tables"
                        :row-key="(record) => record.table"
                        bordered
                        :pagination="false"
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.dataIndex === 'schema_table'">
                                <code>{{ record.schema_table }}</code>
                            </template>
                            <template v-else-if="column.key === 'acao'">
                                <a-tooltip title="Abrir registros">
                                    <a-button
                                        type="primary"
                                        shape="circle"
                                        class="center-flex"
                                        @click="openRecords(record)"
                                    >
                                        <template #icon>
                                            <UnorderedListOutlined />
                                        </template>
                                    </a-button>
                                </a-tooltip>
                            </template>
                        </template>
                    </a-table>
                </a-card>
            </a-config-provider>
        </a-spin>

        <a-modal
            v-model:open="recordsModalVisible"
            :title="selectedTable ? selectedTable.name : 'Registros'"
            :footer="null"
            width="90%"
            destroy-on-close
            wrap-class-name="sinter-records-modal-wrap"
            :body-style="{ maxHeight: '75vh', overflow: 'auto' }"
            @cancel="closeRecords"
        >
            <div class="records-toolbar">
                <a-input-search
                    v-model:value="recordsSearch"
                    placeholder="Filtrar por código ou descrição"
                    allow-clear
                    style="max-width: 320px"
                />
                <a-tooltip title="Novo registro">
                    <a-button
                        type="primary"
                        shape="circle"
                        class="center-flex"
                        @click="openForm()"
                    >
                        <template #icon>
                            <PlusOutlined />
                        </template>
                    </a-button>
                </a-tooltip>
            </div>

            <a-spin :spinning="recordsLoading">
                <a-table
                    :columns="recordColumns"
                    :data-source="filteredRecords"
                    :row-key="(record) => record.id"
                    bordered
                    size="small"
                    :pagination="{ pageSize: 15, showSizeChanger: true }"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.dataIndex === 'ativo'">
                            <a-tag :color="record.ativo ? 'green' : 'red'">
                                {{ record.ativo ? 'Sim' : 'Não' }}
                            </a-tag>
                        </template>
                        <template v-else-if="column.key === 'acao'">
                            <a-tooltip title="Alterar">
                                <a-button
                                    type="primary"
                                    shape="circle"
                                    class="center-flex"
                                    @click="openForm(record)"
                                >
                                    <template #icon>
                                        <FormOutlined />
                                    </template>
                                </a-button>
                            </a-tooltip>
                        </template>
                    </template>
                </a-table>
            </a-spin>
        </a-modal>

        <a-modal
            v-model:open="formModalVisible"
            :title="editingId ? 'Editar Registro' : 'Novo Registro'"
            :mask-closable="false"
            :confirm-loading="formSaving"
            @cancel="closeForm"
        >
            <a-form layout="vertical" :model="form">
                <a-form-item
                    v-for="field in formFields"
                    :key="field.key"
                    :label="field.label"
                    :required="isFieldRequired(field)"
                >
                    <a-input
                        v-if="field.type === 'string'"
                        v-model:value="form[field.key]"
                        :placeholder="field.label"
                        allow-clear
                    />
                    <a-textarea
                        v-else-if="field.type === 'text'"
                        v-model:value="form[field.key]"
                        :placeholder="field.label"
                        :rows="4"
                        allow-clear
                    />
                    <a-select
                        v-else-if="field.type === 'select'"
                        v-model:value="form[field.key]"
                        :placeholder="field.label"
                        style="width: 100%"
                        :options="(field.options || []).map((o) => ({ value: o, label: o }))"
                        allow-clear
                    />
                    <a-switch
                        v-else-if="field.type === 'boolean'"
                        v-model:checked="form[field.key]"
                    />
                </a-form-item>
            </a-form>
            <template #footer>
                <a-space>
                    <a-tooltip title="Cancelar">
                        <a-button shape="circle" class="center-flex" @click="closeForm">
                            <template #icon>
                                <CloseOutlined />
                            </template>
                        </a-button>
                    </a-tooltip>
                    <a-tooltip title="Salvar">
                        <a-button
                            type="primary"
                            shape="circle"
                            class="center-flex"
                            :loading="formSaving"
                            @click="submitForm"
                        >
                            <template #icon>
                                <SaveOutlined />
                            </template>
                        </a-button>
                    </a-tooltip>
                </a-space>
            </template>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
    PlusOutlined,
    FormOutlined,
    UnorderedListOutlined,
    SaveOutlined,
    CloseOutlined,
} from '@ant-design/icons-vue'
import ptBR from 'ant-design-vue/es/locale/pt_BR'
import { message } from 'ant-design-vue'

const apiBase = '/api/sinter/domain-tables'
const currentLocale = ref(ptBR)
const spinning = ref(false)
const tables = ref([])

const recordsModalVisible = ref(false)
const recordsLoading = ref(false)
const recordsSearch = ref('')
const records = ref([])
const selectedTable = ref(null)

const formModalVisible = ref(false)
const formSaving = ref(false)
const editingId = ref(null)
const form = ref({})

const catalogColumns = [
    { title: 'Tabela', dataIndex: 'name', key: 'name' },
    { title: 'Schema / Nome', dataIndex: 'schema_table', key: 'schema_table' },
    { title: 'Ação', key: 'acao', width: 80, align: 'center' },
]

const formFields = computed(() => selectedTable.value?.fields || [])

const recordColumns = computed(() => {
    const fields = formFields.value
    const cols = fields.map((field) => ({
        title: field.label,
        dataIndex: field.key,
        key: field.key,
        ellipsis: field.type === 'text',
        width: field.key === 'codigo' ? 100 : field.key === 'ativo' ? 90 : undefined,
    }))
    cols.push({ title: 'Ação', key: 'acao', width: 80, align: 'center' })
    return cols
})

const filteredRecords = computed(() => {
    if (!recordsSearch.value?.trim()) return records.value || []
    const term = recordsSearch.value.trim().toLowerCase()
    return (records.value || []).filter((row) =>
        Object.values(row).some((value) =>
            value != null && String(value).toLowerCase().includes(term)
        )
    )
})

function isFieldRequired(field) {
    return (field.rules || []).includes('required')
}

function emptyForm() {
    const data = {}
    ;(selectedTable.value?.fields || []).forEach((field) => {
        if (field.type === 'boolean') {
            data[field.key] = true
        } else {
            data[field.key] = undefined
        }
    })
    return data
}

async function loadCatalog() {
    try {
        spinning.value = true
        const { data } = await window.axios.get(apiBase)
        tables.value = data.data || []
    } catch (err) {
        message.error(err.response?.data?.message || 'Erro ao carregar tabelas de código.')
    } finally {
        spinning.value = false
    }
}

async function openRecords(table) {
    selectedTable.value = table
    recordsSearch.value = ''
    records.value = []
    recordsModalVisible.value = true
    await loadRecords()
}

function closeRecords() {
    recordsModalVisible.value = false
    selectedTable.value = null
    records.value = []
    recordsSearch.value = ''
    closeForm()
}

async function loadRecords() {
    if (!selectedTable.value?.table) return
    try {
        recordsLoading.value = true
        const { data } = await window.axios.get(`${apiBase}/${selectedTable.value.table}`)
        records.value = data.data || []
    } catch (err) {
        message.error(err.response?.data?.message || 'Erro ao carregar registros.')
        records.value = []
    } finally {
        recordsLoading.value = false
    }
}

function openForm(record = null) {
    editingId.value = record ? record.id : null
    form.value = emptyForm()
    if (record) {
        ;(selectedTable.value?.fields || []).forEach((field) => {
            form.value[field.key] = record[field.key]
        })
    }
    formModalVisible.value = true
}

function closeForm() {
    formModalVisible.value = false
    editingId.value = null
    form.value = {}
}

async function submitForm() {
    if (!selectedTable.value?.table) return

    for (const field of formFields.value) {
        if (!isFieldRequired(field)) continue
        if (field.type === 'boolean') continue
        const value = form.value[field.key]
        if (value == null || String(value).trim() === '') {
            message.warning(`Informe o campo ${field.label}.`)
            return
        }
    }

    const payload = {}
    formFields.value.forEach((field) => {
        let value = form.value[field.key]
        if (field.type === 'boolean') {
            payload[field.key] = !!value
        } else if (typeof value === 'string') {
            payload[field.key] = value.trim()
        } else {
            payload[field.key] = value
        }
    })

    try {
        formSaving.value = true
        const table = selectedTable.value.table
        if (editingId.value) {
            await window.axios.put(`${apiBase}/${table}/${editingId.value}`, payload)
            message.success('Alterado com sucesso!')
        } else {
            await window.axios.post(`${apiBase}/${table}`, payload)
            message.success('Criado com sucesso!')
        }
        closeForm()
        await loadRecords()
    } catch (err) {
        const errors = err.response?.data?.errors
        const firstError = errors ? Object.values(errors).flat()[0] : null
        message.error(firstError || err.response?.data?.message || 'Erro ao salvar.')
    } finally {
        formSaving.value = false
    }
}

onMounted(loadCatalog)
</script>

<style scoped>
.sinter-tabelas-codigo-page {
    padding: 16px;
    max-width: 1100px;
    margin: 0 auto;
}

.records-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.center-flex {
    display: inline-flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
}
</style>
